import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import { environment } from 'src/environments/environment';
import { IExistingUser, IUserProfile } from '../user-management-interfaces';

// Popup-local extensions: do not change the shared profile contract.
export type PopupProfile = IUserProfile & {
  middleName?: string;
  notes?: string;
  isTemporaryUser?: boolean;
};

export interface SiteUserUpdate {
  siteId: string;
  email: string;
  newEmail?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  phoneNumber?: string;
  notes?: string;
  isDuplicateNameVerified?: boolean;
  mergeWithExistingEmail?: boolean;
}

export type DuplicateProfile = IExistingUser;

export interface SiteUserUpdateResult {
  email: string;
  stagedEmail?: string | null;
  duplicateEmailUsers: DuplicateProfile | null;
  duplicateNameUsers: DuplicateProfile[];
}

// After a cross-role merge: 'confirm' grants PMA/PMU site-wide access, 'assign' picks units.
export interface MergedAccessEvent {
  email: string;
  userType: number;
  mode: 'assign' | 'confirm';
}

export interface MobileAccessInviteResult {
  email: string;
  stagedEmail?: string | null;
  phoneNumber?: string | null;
  invitationPending: boolean;
}

export interface ProfileUpdatedEvent {
  previousEmail: string;
  email: string;
  profileDetails: PopupProfile;
  merged?: boolean;
}

export interface GrantUserAccessRequest {
  siteId: string;
  email: string;
  userType: number;
  entities: Array<{ buildingId: string; floorId: string; unitId: string }>;
  startTime?: string;
  expireTime?: string;
  repeatStartTime?: string;
  repeatEndTime?: string;
  repeatDays?: string[];
}

export interface AddUserAccessRequest {
  siteId: string;
  targetEmail: string;
  entities: Array<{
    buildingId: string;
    floorId: string;
    unitId: string;
    deviceId: string;
  }>;
  startTime?: string;
  expireTime?: string;
  repeatStartTime?: string;
  repeatEndTime?: string;
  repeatDays?: string[];
}

export interface GrantUserAccessResult {
  email: string;
  stagedEmail?: string | null;
  phoneNumber?: string | null;
  invitationPending: boolean;
  canSendInvitation: boolean;
}

interface GraphqlResponse<T> {
  data?: { [operation: string]: T | null };
  errors?: Array<{
    message?: string;
    errorType?: string;
    extensions?: { code?: string };
  }>;
}

export function isGeneratedProfileEmail(email?: string): boolean {
  return !!email?.toLowerCase().endsWith('@unite-user.com');
}

// Display the backend's saved contact; requests always use the identity email.
export function profileEmail(profile?: {
  email?: string;
  stagedEmail?: string | null;
  isTemporaryUser?: boolean | string;
}): string {
  if (profile?.stagedEmail != null) return profile.stagedEmail;
  const temporary = String(profile?.isTemporaryUser).toLowerCase() === 'true';
  return temporary || isGeneratedProfileEmail(profile?.email)
    ? ''
    : profile?.email || '';
}

// Saved phone must already be in +E.164 form, matching what mobileAccessInvite accepts.
export function hasRequiredContact(profile?: {
  email?: string;
  stagedEmail?: string | null;
  phoneNumber?: string;
}): boolean {
  const email = profile?.stagedEmail ?? profile?.email;
  const phone = profile?.phoneNumber;
  return (
    !!email &&
    !isGeneratedProfileEmail(email) &&
    new FormControl(email, MFValidators.email).valid &&
    !!phone &&
    phone.startsWith('+') &&
    new FormControl(phone, MFValidators.usCaPhone).valid
  );
}

export function normalizeProfilePhone(value: string): string {
  const raw = value.trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  return raw.startsWith('+') || digits.length === 11
    ? `+${digits}`
    : `+1${digits}`;
}

export function profilePopupErrorKey(error: any, fallback: string): string {
  const code =
    error?.errorType || error?.error?.errors?.[0]?.errorType || error?.message;
  const keys: { [code: string]: string } = {
    USER_NOT_AUTHORIZED: 'YOU_ARE_NOT_AUTHORIZED_TO_MAKE_THIS_CALL',
    USER_NOT_FOUND: 'USER_NOT_FOUND',
    USER_ALREADY_SIGNED_UP: 'USER_ALREADY_SIGNED_UP',
    PHONE_ALREADY_EXISTS: 'PHONE_NUMBER_ALREADY_EXISTS',
    PHONE_NUMBER_ALREADY_EXISTS: 'PHONE_NUMBER_ALREADY_EXISTS',
    USER_CONTACT_MATCH_CONFLICT: 'PROFILE_POPUP_CONTACT_CONFLICT',
    BAD_ARGUMENT: 'PROFILE_POPUP_CHECK_CONTACT_DETAILS',
  };
  return keys[code] || fallback;
}

@Injectable()
export class ProfileManagementService {
  constructor(private http: HttpClient) {}

  updateSiteUser(variables: SiteUserUpdate): Observable<SiteUserUpdateResult> {
    return this.mutate<SiteUserUpdateResult>(
      'updateSiteUser',
      `mutation UpdateSiteUser($email: String!, $siteId: String!, $newEmail: String, $firstName: String, $middleName: String, $lastName: String, $phoneNumber: String, $notes: String, $isDuplicateNameVerified: Boolean, $mergeWithExistingEmail: Boolean) {
        updateSiteUser(email: $email, siteId: $siteId, newEmail: $newEmail, firstName: $firstName, middleName: $middleName, lastName: $lastName, phoneNumber: $phoneNumber, notes: $notes, isDuplicateNameVerified: $isDuplicateNameVerified, mergeWithExistingEmail: $mergeWithExistingEmail) {
          email
          stagedEmail
          duplicateEmailUsers { email stagedEmail firstName middleName lastName phoneNumber notes userType isTemporaryUser isAssociatedWithSite }
          duplicateNameUsers { email stagedEmail firstName middleName lastName phoneNumber notes userType isTemporaryUser }
        }
      }`,
      {
        ...variables,
        mergeWithExistingEmail: variables.mergeWithExistingEmail === true,
      }
    ).pipe(
      map((result) => {
        if (!result.email || !Array.isArray(result.duplicateNameUsers)) {
          throw new Error('PROFILE_POPUP_SAVE_FAILED');
        }
        return result;
      })
    );
  }

  updatePendingUserNotes(
    siteId: string,
    email: string,
    notes: string
  ): Observable<boolean> {
    return this.mutate<{ success: boolean }>(
      'updatePendingUserNotes',
      `mutation UpdatePendingUserNotes($siteId: String!, $email: String!, $notes: String!) {
        updatePendingUserNotes(siteId: $siteId, email: $email, notes: $notes) { success }
      }`,
      { siteId, email, notes }
    ).pipe(
      map((result) => {
        if (result.success !== true)
          throw new Error('PROFILE_POPUP_SAVE_FAILED');
        return true;
      })
    );
  }

  mobileAccessInvite(
    siteId: string,
    email: string,
    phoneNumber: string
  ): Observable<MobileAccessInviteResult> {
    return this.mutate<MobileAccessInviteResult>(
      'mobileAccessInvite',
      `mutation MobileAccessInvite($siteId: String!, $email: String!, $phoneNumber: String!) {
        mobileAccessInvite(siteId: $siteId, email: $email, phoneNumber: $phoneNumber) {
          email stagedEmail phoneNumber invitationPending
        }
      }`,
      { siteId, email, phoneNumber }
    ).pipe(
      map((result) => {
        if (!result.email || result.invitationPending !== true) {
          throw new Error('PROFILE_POPUP_INVITE_FAILED');
        }
        return result;
      })
    );
  }

  grantUserAccess(
    variables: GrantUserAccessRequest
  ): Observable<GrantUserAccessResult> {
    return this.mutate<GrantUserAccessResult>(
      'grantUserAccess',
      `mutation GrantUserAccess($siteId: String!, $email: String!, $userType: Int!, $entities: [Entity]!, $startTime: String, $expireTime: String, $repeatStartTime: String, $repeatEndTime: String, $repeatDays: [String]) {
        grantUserAccess(siteId: $siteId, email: $email, userType: $userType, entities: $entities, startTime: $startTime, expireTime: $expireTime, repeatStartTime: $repeatStartTime, repeatEndTime: $repeatEndTime, repeatDays: $repeatDays) {
          email stagedEmail phoneNumber invitationPending canSendInvitation
        }
      }`,
      variables
    ).pipe(
      map((result) => {
        if (!result.email || typeof result.canSendInvitation !== 'boolean') {
          throw new Error('FAILED_TO_SHARE_ACCESS_SERVER_ERROR');
        }
        return result;
      })
    );
  }

  addUserAccess(
    variables: AddUserAccessRequest
  ): Observable<{ success: boolean }> {
    return this.mutate<{ success: boolean }>(
      'addUserAccess',
      `mutation AddUserAccess($siteId: String!, $targetEmail: String!, $entities: [Entity!]!, $startTime: String, $expireTime: String, $repeatStartTime: String, $repeatEndTime: String, $repeatDays: [String]) {
        addUserAccess(siteId: $siteId, targetEmail: $targetEmail, entities: $entities, startTime: $startTime, expireTime: $expireTime, repeatStartTime: $repeatStartTime, repeatEndTime: $repeatEndTime, repeatDays: $repeatDays) { success }
      }`,
      variables
    ).pipe(
      map((result) => {
        if (result.success !== true)
          throw new Error('FAILED_TO_SHARE_ACCESS_SERVER_ERROR');
        return result;
      })
    );
  }

  private mutate<T>(
    operation: string,
    query: string,
    variables: object
  ): Observable<T> {
    return this.http
      .post<GraphqlResponse<T>>(environment.graphqlUrl, { query, variables })
      .pipe(
        map((response) => {
          // GraphQL can return HTTP 200 with partial data AND errors.
          if (response.errors?.length) {
            const failure = response.errors[0];
            throw Object.assign(
              new Error(failure.message || 'PROFILE_POPUP_REQUEST_FAILED'),
              {
                errorType: failure.errorType || failure.extensions?.code,
              }
            );
          }
          const result = response.data?.[operation];
          if (!result) throw new Error('PROFILE_POPUP_REQUEST_FAILED');
          return result;
        })
      );
  }
}
